import { ServiceIdSchema, UserIdSchema } from "@core/domains/identity";
import { identity } from "@core/foundation/function";
import {
  createModel,
  createModelId,
  createModelSchemas,
} from "@core/foundation/model";
import { z } from "@core/zod";

import { ID_PREFIX } from "./shared/id-prefix.ts";

const ReservationStatusSchema = z.enum(["committed", "pending", "released"]);

const { generateId: generateReservationId, idSchema: ReservationIdSchema } =
  createModelId({ idPrefix: ID_PREFIX.RESERVATION });

export { ReservationIdSchema };

export type ReservationId = z.infer<typeof ReservationIdSchema>;

export const ReservationMetadataSchema = z.record(z.string(), z.unknown());

export type ReservationMetadata = z.infer<typeof ReservationMetadataSchema>;

export const Reservation = createModel({
  create: (item, schema) => {
    return {
      ...schema.decode(item),
      actualAmount: null,
      id: generateReservationId(),
    };
  },
  schemas: createModelSchemas({
    base: z.object(
      {
        actualAmount: z.decimal().nonnegative().nullable(),
        amount: z.decimal().positive(),
        description: z.string(),
        etag: z.string().optional(),
        expiresAt: z.iso.datetime(),
        id: ReservationIdSchema,
        metadata: ReservationMetadataSchema,
        metadataPrincipalId: ServiceIdSchema,
        principalId: UserIdSchema,
        status: ReservationStatusSchema,
      },
      {
        actualAmount: "The credit the commit charged, or null until committed.",
        amount: "The credit the reservation holds, as a decimal string.",
        description: "What the hold is for, in words a person reads.",
        etag: "The reservation's version, stamped by storage for concurrency.",
        expiresAt:
          "When the reservation's hold expires, as an ISO 8601 timestamp.",
        id: "The unique identifier for the reservation.",
        metadata: "The owner's record of the work the hold pays for.",
        metadataPrincipalId:
          "The id of the service that authored the metadata and owns the hold.",
        principalId: "The id of the user whose credit the reservation holds.",
        status: "The reservation's status: pending, committed, or released.",
      },
    ),
    defaults: { metadata: {} },
    derive: {
      create: schema =>
        schema.omit({ actualAmount: true, etag: true, id: true }),
      recovery: identity,
      update: schema =>
        schema
          .pick({
            actualAmount: true,
            description: true,
            metadata: true,
            status: true,
          })
          .partial(),
    },
  }),
  update: (item, update, schema) => {
    return { ...item, ...schema.decode(update) };
  },
});

export interface Reservation extends z.infer<typeof Reservation.schema> {}
